import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
// Direct client import for the OTP / reset / gate paths (matches AcceptInvitePage —
// this is an auth screen). The password sign-in itself stays on the AuthContext seam.
import { supabase } from '../auth/supabaseClient';
import { fetchSignupMode, isSignupOpen } from '../utils/signupPolicy';

/**
 * Capture login — ported from Library's src/pages/LoginPage.jsx (canonical),
 * magic-link-first with password as the secondary mode. Library parity, with
 * Capture specifics kept: "Capture" product name and navigate(from) return-path
 * behavior. The "Create an account" link is gated on server policy exactly as
 * Library gates it — see the signupMode effect below.
 *
 * Modes:
 *   1. magic_link (default). User enters email → Supabase OTP → clicks magic
 *      link → lands on '/' with a session (detectSessionInUrl) → RequireAuth
 *      admits. shouldCreateUser is FALSE here (vs. /invites/accept where the
 *      invite authorizes creation) so /login cannot mint new auth identities.
 *   2. password (secondary, legacy). Existing flow preserved. After a
 *      successful Supabase auth, fetch /api/library/bootstrap (same-origin
 *      proxy seam) and enforce the tenant's allowed_auth_providers — if
 *      `password` is not allowed, sign the user out with a clear message.
 *   3. forgot_password (reached from password mode). Sends a Supabase reset
 *      email that lands on LIBRARY's /reset-password page — Capture has no
 *      reset page; the Supabase identity is shared across both apps.
 */

// Sentinel for the tenant password gate: thrown when the tenant's
// allowed_auth_providers excludes 'password'. The bootstrap catch re-throws on
// instanceof (message-prefix matching was fragile). TANDEM with Library's
// LoginPage.jsx per decisions/shared/2026-07-16-auth-surface-canonical-pattern.md.
class PasswordNotAllowedError extends Error {
  constructor() {
    super('Password sign-in is not enabled for your organization. Use the magic link option instead.');
    this.name = 'PasswordNotAllowedError';
  }
}

// Supabase's "user does not exist" class of signInWithOtp error under
// shouldCreateUser:false (the "Signups not allowed for otp" family). Masked to
// the normal confirmation — surfacing it would be an email-enumeration oracle,
// matching how the forgot-password path already masks. Other OTP errors (rate
// limit, network) still display.
function isOtpUserNotFoundError(err) {
  const msg = String(err?.message || '').toLowerCase();
  return err?.code === 'otp_disabled' || msg.includes('signups not allowed');
}

export default function Login() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  // mode controls which form is rendered. 'magic_link' is the default.
  // 'forgot_password' is reached from a link in the 'password' mode.
  const [mode, setMode] = useState('magic_link');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  // Server config (cosmetic — the backend is the real gate). Show
  // "Create an account" only when signups are open. Library parity:
  // src/pages/LoginPage.jsx does the same against the same endpoint.
  const [signupMode, setSignupMode] = useState(null);

  // Read the signup policy from the server so flipping SIGNUP_MODE needs no
  // rebuild. Fail closed: fetchSignupMode resolves to null on any error,
  // non-2xx, or missing field, which leaves the link hidden. Before this gate
  // existed, Capture advertised signup while SIGNUP_MODE=allowlist, sending
  // people through a form that provisions nothing and lands them on
  // "No access yet".
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const mode = await fetchSignupMode();
      if (cancelled) return;
      setSignupMode(mode);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // If the user lands here with an existing session (previously signed in —
  // the magic-link return itself targets '/', not /login), forward them in.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (cancelled) return;
        if (data?.session) {
          navigate(from, { replace: true });
        }
      } catch {
        // Best-effort. If session lookup fails, fall through to the form.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, from]);

  const switchMode = (next) => {
    setMode(next);
    setError(null);
    setOtpSent(false);
    setResetSent(false);
  };

  const handleMagicLinkSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const trimmedEmail = String(email || '').trim();
    if (!trimmedEmail) {
      setLoading(false);
      setError(new Error('Please enter your email address.'));
      return;
    }

    try {
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: trimmedEmail,
        options: {
          // The magic link lands on '/' on THIS origin; detectSessionInUrl
          // hydrates the session and RequireAuth admits. No routing changes.
          emailRedirectTo: `${window.location.origin}/`,
          // MUST be false on login — never create accounts from the login
          // page (unlike /invites/accept, where the invite authorizes it).
          shouldCreateUser: false,
        },
      });

      if (otpError) throw otpError;

      // Always show the same confirmation regardless of whether the email
      // matched an existing user. Showing a "no account found" error would
      // be an email-enumeration vulnerability.
      setOtpSent(true);
    } catch (err) {
      if (isOtpUserNotFoundError(err)) {
        // Same confirmation as success — see enumeration comment above. Log
        // the real error for ops visibility only.
        console.warn('[Login] signInWithOtp (masked):', err?.message || err);
        setOtpSent(true);
      } else {
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const signInData = await signIn(email, password);

      // Password gate (Library parity). Supabase doesn't expose tenant context
      // pre-auth, so we authenticate first then check via the same-origin
      // /api/library/bootstrap seam. If the tenant has disabled `password`,
      // sign out and surface a clear error. Only fires for the password path —
      // magic-link sign-ins skip this entirely (the password gate is irrelevant).
      const accessToken = signInData?.session?.access_token;
      if (accessToken) {
        try {
          const res = await fetch('/api/library/bootstrap', {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          if (res.ok) {
            const bootstrap = await res.json();
            const providers = Array.isArray(bootstrap?.tenant?.allowedAuthProviders)
              ? bootstrap.tenant.allowedAuthProviders
              : [];
            if (providers.length > 0 && !providers.includes('password')) {
              await supabase.auth.signOut();
              throw new PasswordNotAllowedError();
            }
          }
        } catch (bootstrapErr) {
          if (bootstrapErr instanceof PasswordNotAllowedError) {
            throw bootstrapErr;
          }
          // Network glitch / bootstrap 5xx — let the user proceed. Denying
          // access on infrastructure errors would be worse than the rare
          // false-allow; the app re-runs bootstrap after login anyway.
        }
      }

      navigate(from, { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const trimmedEmail = String(email || '').trim();
    if (!trimmedEmail) {
      setLoading(false);
      setError(new Error('Please enter your email address.'));
      return;
    }

    try {
      // Library owns the reset page — Capture has no /reset-password route.
      // The Supabase identity is shared, so a reset completed on the Library
      // origin also fixes the password used here.
      const libraryOrigin = process.env.REACT_APP_LIBRARY_APP_ORIGIN || 'https://app.steliad.com';
      const redirectTo = `${libraryOrigin}/reset-password`;
      const { error: resetErr } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
        redirectTo,
      });
      // Per Amendment A1.2 (Library parity): always show the same "check your
      // email" confirmation, even if the email doesn't exist or Supabase
      // returns an error — same email-enumeration protection as the
      // magic-link flow. We do still log Supabase errors to the console for
      // ops visibility.
      if (resetErr) {
        console.warn('[Login] resetPasswordForEmail returned error:', resetErr.message);
      }
      setResetSent(true);
    } catch (err) {
      console.warn('[Login] resetPasswordForEmail threw:', err);
      // Still show the same confirmation — see comment above.
      setResetSent(true);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Render ----------

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>

        {/* Logo + product name */}
        <div style={styles.brand}>
          <img src="/steliad_logo_navy.png" alt="Steliad" style={styles.logo} />
          <span style={styles.productName}>Capture</span>
        </div>

        <h2 style={styles.title}>Sign In</h2>

        {error && (
          <div style={styles.error}>
            {String(error.message || error)}
          </div>
        )}

        {/* Magic-link "check your email" confirmation */}
        {mode === 'magic_link' && otpSent && (
          <div style={styles.confirm}>
            <div style={styles.confirmTitle}>Check your email.</div>
            <p style={styles.confirmBody}>
              We just sent a one-time sign-in link to <strong>{email}</strong>.
              Click the link in that email to finish signing in.
            </p>
            <p style={styles.fine}>
              You can close this tab — the link will bring you back here automatically.
            </p>
            <button
              type="button"
              onClick={() => { setOtpSent(false); setError(null); }}
              style={styles.linkButton}
            >
              Use a different email
            </button>
          </div>
        )}

        {/* Magic-link form (primary) */}
        {mode === 'magic_link' && !otpSent && (
          <>
            <p style={styles.lead}>
              We'll email you a one-time sign-in link. No password needed.
            </p>
            <form onSubmit={handleMagicLinkSubmit}>
              <label style={styles.label}>
                Email
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  style={styles.input}
                  placeholder="you@company.com"
                />
              </label>

              <button
                type="submit"
                disabled={loading}
                style={{
                  ...styles.button,
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Sending link…' : 'Send sign-in link'}
              </button>
            </form>

            <div style={styles.altRow}>
              <button
                type="button"
                onClick={() => switchMode('password')}
                style={styles.linkButton}
              >
                Sign in with password instead
              </button>
            </div>
          </>
        )}

        {/* Password form (secondary, legacy) */}
        {mode === 'password' && (
          <>
            <form onSubmit={handlePasswordSubmit}>
              <label style={styles.label}>
                Email
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  style={styles.input}
                  placeholder="you@company.com"
                />
              </label>

              <label style={styles.label}>
                Password
                <div style={styles.passwordWrapper}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    style={styles.passwordInput}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={styles.toggleButton}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </label>

              <button
                type="submit"
                disabled={loading}
                style={{
                  ...styles.button,
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>

            <div style={styles.altRow}>
              <button
                type="button"
                onClick={() => switchMode('forgot_password')}
                style={styles.linkButton}
              >
                Forgot password?
              </button>
            </div>
            <div style={styles.altRow}>
              <button
                type="button"
                onClick={() => switchMode('magic_link')}
                style={styles.linkButton}
              >
                ← Back to email sign-in link
              </button>
            </div>
          </>
        )}

        {/* Forgot-password panel (reached from the password panel; not exposed
            from the magic-link panel because that flow doesn't use passwords). */}
        {mode === 'forgot_password' && resetSent && (
          <div style={styles.confirm}>
            <div style={styles.confirmTitle}>Check your email.</div>
            <p style={styles.confirmBody}>
              If <strong>{email}</strong> matches an account, we just sent a password
              reset link to that address.
            </p>
            <p style={styles.fine}>
              The link in the email opens a page where you can choose a new password.
            </p>
            <button
              type="button"
              onClick={() => switchMode('password')}
              style={styles.linkButton}
            >
              ← Back to sign in
            </button>
          </div>
        )}

        {mode === 'forgot_password' && !resetSent && (
          <>
            <p style={styles.lead}>
              Enter your email and we'll send you a link to reset your password.
            </p>
            <form onSubmit={handleForgotPasswordSubmit}>
              <label style={styles.label}>
                Email
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  style={styles.input}
                  placeholder="you@company.com"
                />
              </label>

              <button
                type="submit"
                disabled={loading}
                style={{
                  ...styles.button,
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Sending reset link…' : 'Send reset link'}
              </button>
            </form>

            <div style={styles.altRow}>
              <button
                type="button"
                onClick={() => switchMode('password')}
                style={styles.linkButton}
              >
                ← Back to sign in
              </button>
            </div>
          </>
        )}

        {isSignupOpen(signupMode) && (
          <div style={styles.links}>
            <a
              href={process.env.REACT_APP_SIGNUP_URL || '/signup'}
              style={styles.link}
            >
              Create an account
            </a>
          </div>
        )}

      </div>
    </div>
  );
}

// Inline SVG icons — no external dependency required
function EyeIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

// Styles ported from Library's LoginPage.jsx (canonical) — keep in sync.
const styles = {
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#f1f5f9',
  },
  card: {
    background: '#fff',
    borderRadius: '0.75rem',
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    padding: '2.5rem 2rem',
    width: '100%',
    maxWidth: '400px',
  },
  brand: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    marginBottom: '1.75rem',
  },
  logo: {
    height: '56px',
    width: 'auto',
  },
  productName: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#94a3b8',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#1e293b',
    marginBottom: '0.5rem',
    marginTop: 0,
    textAlign: 'center',
  },
  lead: {
    fontSize: '0.9rem',
    color: '#64748b',
    marginTop: 0,
    marginBottom: '1.25rem',
    textAlign: 'center',
  },
  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#475569',
    marginBottom: '1rem',
  },
  input: {
    display: 'block',
    width: '100%',
    padding: '0.625rem 0.75rem',
    marginTop: '0.25rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.5rem',
    fontSize: '0.95rem',
    outline: 'none',
    boxSizing: 'border-box',
  },
  passwordWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '0.25rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxSizing: 'border-box',
  },
  passwordInput: {
    flex: 1,
    padding: '0.625rem 0.75rem',
    border: 'none',
    fontSize: '0.95rem',
    outline: 'none',
    boxSizing: 'border-box',
  },
  toggleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 0.75rem',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#94a3b8',
    flexShrink: 0,
  },
  button: {
    display: 'block',
    width: '100%',
    padding: '0.75rem',
    marginTop: '0.5rem',
    background: 'var(--stm-accent-primary)',
    color: 'var(--stm-text-on-accent)',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    fontWeight: 500,
  },
  altRow: {
    marginTop: '1rem',
    textAlign: 'center',
  },
  linkButton: {
    background: 'transparent',
    border: 'none',
    color: 'var(--stm-accent-info)',
    fontSize: '0.875rem',
    cursor: 'pointer',
    padding: 0,
    textDecoration: 'underline',
  },
  confirm: {
    background: '#f0f9ff',
    border: '1px solid #bae6fd',
    borderRadius: '0.5rem',
    padding: '1rem',
    marginBottom: '1rem',
  },
  confirmTitle: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#0c4a6e',
    marginBottom: '0.5rem',
  },
  confirmBody: {
    fontSize: '0.875rem',
    color: '#0f172a',
    margin: 0,
    marginBottom: '0.75rem',
  },
  fine: {
    fontSize: '0.75rem',
    color: '#64748b',
    margin: 0,
    marginBottom: '0.75rem',
  },
  error: {
    background: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
    borderRadius: '0.5rem',
    padding: '0.75rem',
    marginBottom: '1rem',
    fontSize: '0.875rem',
  },
  links: {
    marginTop: '1.5rem',
    textAlign: 'center',
    fontSize: '0.875rem',
    color: '#94a3b8',
  },
  link: {
    color: 'var(--stm-accent-info)',
    textDecoration: 'none',
  },
};
